import React from 'react';
import { X } from 'lucide-react';
import { SacredArtpiece } from '../../constants/sacredArt';

interface SacredArtInfoModalProps {
  artpiece: SacredArtpiece;
  isOpen: boolean;
  onClose: () => void;
}

export const SacredArtInfoModal: React.FC<SacredArtInfoModalProps> = ({
  artpiece,
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-96 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Artwork Details</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Title */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Title</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">{artpiece.title}</p>
          </div>

          {/* Artist */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Artist</p>
            <p className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-1">{artpiece.artist}</p>
          </div>

          {/* Year */}
          {artpiece.year && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Year</p>
              <p className="text-base text-gray-700 dark:text-gray-300 mt-1">{artpiece.year}</p>
            </div>
          )}

          {/* Theme */}
          {artpiece.theme && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Theme</p>
              <p className="inline-block px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-sm font-medium rounded-full mt-1">
                {artpiece.theme}
              </p>
            </div>
          )}

          {/* Description */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Description</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mt-2">
              {artpiece.description}
            </p>
          </div>

          {/* Close Button */}
          <div className="pt-4">
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-2 rounded-lg font-medium transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
